import Field from 'components/Form/Field';
import {FormContextString, InputProps} from 'components/Form/types';
import {getRegisterRules} from 'components/Form/utils';
import useFormError from 'hooks/useFormErrors';
import {FC, ReactNode} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';

export type InputSanitizedProps = {
    sanitizer: (value: string) => string;
    suffix?: ReactNode;
} & InputProps;

const InputSanitized: FC<InputSanitizedProps> = ({
    className,
    classNameInput,
    disabled,
    error,
    helpText,
    id,
    isCompact,
    label,
    name,
    onChange,
    readOnly,
    required,
    requiredType,
    rules,
    sanitizer,
    suffix,
    ...rest
}) => {
    const {control} = useFormContext<FormContextString>();

    const registerRules = getRegisterRules({disabled, required, rules});

    const fieldError = useFormError(name, error);

    return (
        <Field
            className={className}
            disabled={registerRules?.disabled || readOnly}
            error={fieldError}
            helpText={helpText}
            isCompact={isCompact}
            label={label}
            name={name}
            required={!!registerRules?.required}
            requiredType={requiredType}
            type="input"
        >
            <Controller
                control={control}
                name={name}
                render={({
                    field: {onChange: onFieldChange, value, ...fieldRest},
                }) => (
                    <div className="flex flex-row flex-nowrap items-center gap-2">
                        <input
                            aria-label={
                                rest['aria-label'] || label === null
                                    ? undefined
                                    : typeof label === 'string'
                                      ? label
                                      : name
                            }
                            className={twMerge(
                                isCompact && 'h-8 text-sm',
                                fieldError && 'input-invalid',
                                classNameInput,
                            )}
                            id={id || name}
                            onChange={
                                registerRules?.disabled || readOnly
                                    ? undefined
                                    : (event) => {
                                          onFieldChange(
                                              sanitizer(event.target.value),
                                          );
                                      }
                            }
                            {...rest}
                            {...fieldRest}
                            disabled={registerRules?.disabled || readOnly}
                            type="text"
                            value={value || ''}
                        />
                        {suffix && <div>{suffix}</div>}
                    </div>
                )}
                rules={{
                    ...registerRules,
                    onChange,
                }}
            />
        </Field>
    );
};

export default InputSanitized;
